import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { useAuthStore } from '@/store/authStore';
import { timeAgo } from '@/lib/format';

interface MyComment {
  id: number;
  post_id: number;
  content: string;
  is_blinded: boolean;
  created_at: string;
  like_count: number;
  dislike_count: number;
  post_title: string;
  board_slug: string;
  board_name: string;
}

export function MyCommentsPage() {
  const navigate = useNavigate();
  const { me, isLoggedIn } = useAuthStore();

  const { data, isLoading } = useQuery({
    queryKey: ['users', me?.userId, 'comments'],
    queryFn: () => get<{ comments: MyComment[] }>(`/users/${me!.userId}/comments`),
    enabled: isLoggedIn && !!me?.userId,
  });

  return (
    <div className="container-base py-10">
      <div className="mb-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/me')}
          className="text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
          aria-label="뒤로"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-extrabold">내 댓글</h1>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      )}

      {data?.comments && data.comments.length > 0 ? (
        <ul className="divide-y divide-[var(--color-border)] rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          {data.comments.map((c) => (
            <li key={c.id} className="p-4">
              <Link
                to={`/post/${c.post_id}`}
                className="block hover:bg-[var(--color-bg)] -m-4 p-4 rounded-lg"
              >
                <div className="mb-1 flex items-center gap-2 text-xs text-[var(--color-text-sub)]">
                  <span className="rounded-full bg-[var(--color-bg)] px-2 py-0.5">
                    {c.board_name}
                  </span>
                  <span className="truncate font-medium text-[var(--color-text)]">
                    {c.post_title}
                  </span>
                </div>
                <p className="line-clamp-2 text-sm">
                  {c.is_blinded ? '(블라인드 처리된 댓글)' : c.content}
                </p>
                <div className="mt-2 flex gap-3 text-xs text-[var(--color-text-sub)]">
                  <span>{timeAgo(c.created_at)}</span>
                  <span>추천 {c.like_count}</span>
                  {c.dislike_count > 0 && <span>비추 {c.dislike_count}</span>}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        !isLoading && (
          <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-[var(--color-text-sub)]">
            작성한 댓글이 없어요.
          </div>
        )
      )}
    </div>
  );
}
